/**
 * 팝업 호스트 — Layout에 마운트되어 활성 팝업을 모두 렌더링.
 *
 * 동작:
 *  1. useLocation로 현재 경로 추적
 *  2. useActivePopups로 매칭 팝업 조회
 *  3. dismiss 필터링 (세션 + 기간/영구)
 *  4. display_type별 priority 최상위 1개씩만 표시
 *
 * 영구지침: docs/develop/popup/popup-spec.md §9.2
 */
import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useActivePopups } from '@/features/popup/hooks/useActivePopups'
import { isDismissed } from '@/lib/popup/dismissStorage'
import type { PopupResponse, PopupDisplayType } from '@/features/popup/types'
import TopBanner from './TopBanner'
import CenterModal from './CenterModal'
import BottomSheet from './BottomSheet'

export default function PopupHost() {
  const location = useLocation()
  const { data: popups = [] } = useActivePopups(location.pathname)

  // dismiss 필터 + display_type별 1개씩 선택
  const visiblePopups = useMemo<PopupResponse[]>(() => {
    const filtered = popups.filter((p) => !isDismissed(p.id))

    // 백엔드가 priority DESC 정렬해서 보내므로 첫 번째가 최상위
    const byType = new Map<PopupDisplayType, PopupResponse>()
    for (const popup of filtered) {
      if (!byType.has(popup.displayType)) {
        byType.set(popup.displayType, popup)
      }
    }
    return Array.from(byType.values())
  }, [popups])

  return (
    <>
      {visiblePopups.map((popup) => {
        switch (popup.displayType) {
          case 'TOP_BANNER':
            return <TopBanner key={popup.id} popup={popup} />
          case 'CENTER_MODAL':
            return <CenterModal key={popup.id} popup={popup} />
          case 'BOTTOM_SHEET':
            return <BottomSheet key={popup.id} popup={popup} />
          default:
            return null
        }
      })}
    </>
  )
}
